/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// Individual onboarding (owner, 21/10/2026): the firm's document thresholds for
// individual clients, read and saved through /api/individual-thresholds (MLRO
// only; every change in the firm's change history). Shown only to a firm with
// individual clients switched on. What is in force is always stated, with where
// it came from: the firm's own values or the platform defaults.
import React, { useEffect, useState } from 'react';

const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('en-GB') : '');

export default function IndividualThresholdsCard({ firmId, headers }) {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ sofDocument: '', fullSofSow: '', currency: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const call = async (body) => {
    const r = await fetch('/api/individual-thresholds', { method: 'POST', headers: await headers(), body: JSON.stringify({ firmId, ...body }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.success) throw new Error(j.error || 'The thresholds could not be read.');
    return j;
  };
  useEffect(() => {
    let live = true;
    if (!firmId) return undefined;
    call({ action: 'read' }).then((j) => {
      if (!live) return;
      setState(j);
      const s = j.stored || {};
      setForm({ sofDocument: s.sofDocument ?? '', fullSofSow: s.fullSofSow ?? '', currency: s.currency || '' });
    }).catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [firmId]);

  const run = async (body, done) => {
    setBusy(true); setErr(null); setMsg(null);
    try { const j = await call(body); setState(j); setMsg(done + (j.warning ? ' ' + j.warning : '')); }
    catch (e) { setErr(e.message + ' Nothing was changed.'); }
    setBusy(false);
  };

  const t = state && state.thresholds;
  const field = { width: '100%', padding: '7px 10px', background: 'var(--lc25)', border: '1px solid var(--lc30)', borderRadius: 6, color: 'var(--lc7)', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' };
  return (
    <div data-individual-thresholds style={{ marginBottom: 14, paddingTop: 12, borderTop: '1px solid var(--lc6)' }}>
      <div style={{ fontSize: 12, color: 'var(--lc2)', fontWeight: 600, marginBottom: 2 }}>Document thresholds for individual clients</div>
      <div style={{ fontSize: 10.5, color: 'var(--lc14)', marginBottom: 8, lineHeight: 1.5 }}>
        Above the first amount (expected deposits in the first twelve months), an individual client is asked for one source of funds document. Above the second, for full source of funds and source of wealth documents. The client is never told the amounts.
      </div>
      {t ? (
        <div style={{ fontSize: 11, color: 'var(--lc4)', marginBottom: 8 }}>
          In force: {t.currency} {fmt(t.sofDocument)} and {t.currency} {fmt(t.fullSofSow)}, {t.source === 'policy' ? 'the firm’s own values' : 'the platform defaults'}.
        </div>
      ) : !err && <div style={{ fontSize: 11, color: 'var(--lc19)', marginBottom: 8 }}>Reading the thresholds...</div>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ flex: '1 1 120px', fontSize: 10.5, color: 'var(--lc14)' }}>One document above
          <input aria-label="One source of funds document above" inputMode="numeric" value={form.sofDocument} onChange={(e) => setForm((f) => ({ ...f, sofDocument: e.target.value }))} style={field} />
        </label>
        <label style={{ flex: '1 1 120px', fontSize: 10.5, color: 'var(--lc14)' }}>Full documents above
          <input aria-label="Full source of funds and source of wealth documents above" inputMode="numeric" value={form.fullSofSow} onChange={(e) => setForm((f) => ({ ...f, fullSofSow: e.target.value }))} style={field} />
        </label>
        <label style={{ flex: '0 1 80px', fontSize: 10.5, color: 'var(--lc14)' }}>Currency
          <input aria-label="Currency" maxLength={3} placeholder={t ? t.currency : ''} value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} style={field} />
        </label>
      </div>
      {err && <div style={{ fontSize: 11.5, color: 'var(--lc92)', marginBottom: 8, lineHeight: 1.5 }}>{err}</div>}
      {msg && <div style={{ fontSize: 11.5, color: 'var(--lc2)', marginBottom: 8, lineHeight: 1.5 }}>{msg}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button disabled={busy} onClick={() => run({ action: 'save', ...form }, 'Saved. This applies to individual files built from now on.')} style={{ background: 'var(--lc8)', border: 'none', color: 'var(--lc18)', borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>Save thresholds</button>
        <button disabled={busy || !(state && state.stored)} onClick={() => run({ action: 'clear' }, 'The platform defaults now apply.')} style={{ background: 'transparent', border: '1px solid var(--lc29)', color: 'var(--lc4)', borderRadius: 6, padding: '7px 12px', fontSize: 12, cursor: busy ? 'default' : 'pointer', opacity: (busy || !(state && state.stored)) ? 0.5 : 1 }}>Use the platform defaults</button>
      </div>
    </div>
  );
}
